import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import AlertBanner from "@/components/AlertBanner";
import MatrixStockTable from "@/components/MatrixStockTable";
import HistoryCharts from "@/components/HistoryCharts";
import MovementsList from "@/components/MovementsList";
import OperationDialog from "@/components/OperationDialog";
import StockDeclarationDialog from "@/components/StockDeclarationDialog";
import BesoinDialog from "@/components/BesoinDialog";
import { OPS, OP_MODULE } from "@/lib/ops";
import { Package, CalendarClock, ClipboardList, BellRing } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Truck, Inbox, Droplets, Split, PackageCheck, AlertTriangle, FileDown, FileText,
} from "lucide-react";

const ICONS = { Truck, Inbox, Droplets, Split, PackageCheck, AlertTriangle };
const ACCENT_BTN = {
  amber: "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100",
  green: "border-green-300 bg-green-50 text-green-900 hover:bg-green-100",
  red: "border-red-300 bg-red-50 text-red-900 hover:bg-red-100",
};

export default function Dashboard() {
  const { user, can } = useAuth();
  const [op, setOp] = useState(null);
  const [showDecl, setShowDecl] = useState(false);
  const [showBesoin, setShowBesoin] = useState(false);
  const [editMovement, setEditMovement] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const canDeclare = can("stock_declaration", "create");
  const canBesoin = can("besoin", "create");
  const canViewDecl = can("stock_declaration", "view") || canDeclare;
  const { data: declStatus, refetch: refetchDecl } = useQuery({
    queryKey: ["decl-status-dash", refreshKey],
    queryFn: async () => (await api.get("/stock-declarations/status")).data,
    enabled: canViewDecl,
  });
  const myFerme = (declStatus?.fermes || [])[0];
  const isFerme = user?.role === "ferme_production";
  const reminderKey = myFerme ? `friday-reminder-${myFerme.site}-${new Date().toLocaleDateString("fr-CA", { timeZone: "Europe/Paris" })}` : "";
  const [reminderSeen, setReminderSeen] = useState(false);
  const showReminderPopup = isFerme && canDeclare && myFerme?.reminder && !reminderSeen && !localStorage.getItem(reminderKey);
  const dismissReminder = () => { localStorage.setItem(reminderKey, "1"); setReminderSeen(true); };
  const ecartFermes = (declStatus?.fermes || []).filter((f) => f.has_ecart);
  const fmtFriday = (iso, opts) => {
    return fmtDay(iso, opts);
  };

  const { data: refs } = useQuery({
    queryKey: ["refs"],
    queryFn: async () => {
      const [s, t] = await Promise.all([api.get("/sites"), api.get("/packaging-types")]);
      return { sites: s.data, types: t.data };
    },
  });
  const sites = refs?.sites || [];
  const types = refs?.types || [];

  const { data: stockData, refetch, isLoading: stocksLoading } = useQuery({
    queryKey: ["stocks", refreshKey],
    queryFn: async () => (await api.get("/stocks")).data,
    enabled: can("stocks", "view"),
  });

  const matrix = stockData?.matrix || [];
  const alerts = stockData?.alerts || [];

  const gestionCodes = new Set(["ANNEZIN", "DDO"]);

  const actions = Object.entries(OPS).filter(([type, o]) => !o.hidden && can(OP_MODULE[type], "create"));
  const canOperate = actions.length > 0;

  const bump = () => setRefreshKey((k) => k + 1);

  const download = async (path, filename) => {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openOp = (type) => { setEditMovement(null); setOp(type); };
  const openEdit = (m) => { setEditMovement(m); setOp(m.type); };

  const tabs = [];
  if (can("stocks", "view")) tabs.push("stocks");
  if (can("history", "view")) tabs.push("history");
  if (can("operations", "view")) tabs.push("saisies");

  return (
    <AppShell>
      <div className="space-y-5">
        {can("stocks", "view") && <AlertBanner alerts={alerts} />}

        {canViewDecl && ecartFermes.length > 0 && (
          <div className="w-full rounded-xl border-2 border-amber-400 bg-amber-50 p-4 shadow-sm" data-testid="declaration-ecart-banner">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="h-6 w-6 text-amber-600 shrink-0" strokeWidth={2.4} />
              <span className="font-display font-extrabold uppercase tracking-tight text-amber-800 text-lg">
                {ecartFermes.length} écart{ecartFermes.length > 1 ? "s" : ""} de déclaration
              </span>
            </div>
            <p className="text-sm text-amber-800 mb-2">
              Stock déclaré très différent du stock calculé (seuil {declStatus?.ecart_threshold_pct ?? 20}%). À vérifier :
            </p>
            <div className="flex flex-wrap gap-2">
              {ecartFermes.map((f) => (
                <span key={f.site} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900">
                  ⚠️ {f.site_name}
                </span>
              ))}
            </div>
          </div>
        )}

        {canDeclare && isFerme && myFerme?.reminder && (
          <button
            onClick={() => setShowDecl(true)}
            data-testid="friday-reminder-banner"
            className="w-full text-left rounded-xl border-2 border-blue-300 bg-blue-50 p-4 flex items-start gap-3 tactile hover:bg-blue-100"
          >
            <BellRing className="h-6 w-6 text-blue-700 shrink-0 mt-0.5" strokeWidth={2.2} />
            <div>
              <p className="font-display font-bold text-blue-900">Rappel du vendredi : déclarez votre stock aujourd'hui</p>
              <p className="text-sm text-blue-800">La déclaration de stock de <strong>{myFerme.site_name}</strong> pour ce vendredi n'est pas encore faite. Touchez ici pour la saisir en piles.</p>
            </div>
          </button>
        )}

        {canDeclare && isFerme && myFerme && !myFerme.declared && (
          <button
            onClick={() => setShowDecl(true)}
            data-testid="friday-declaration-reminder"
            className="w-full text-left rounded-xl border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3 tactile hover:bg-red-100"
          >
            <CalendarClock className="h-6 w-6 text-red-600 shrink-0 mt-0.5" strokeWidth={2.2} />
            <div>
              <p className="font-display font-bold text-red-900">🔴 {myFerme.late ? "Déclaration EN RETARD" : "Stock non déclaré"}</p>
              <p className="text-sm text-red-800">
                Dernière déclaration attendue : <strong className="capitalize">{fmtFriday(myFerme.expected_friday)}</strong> pour <strong>{myFerme.site_name}</strong>. Touchez ici pour déclarer votre stock en piles.
              </p>
            </div>
          </button>
        )}

        {(canOperate || canDeclare || canBesoin) && (
          <div>
            <h2 className="font-display font-extrabold text-xl text-slate-900 mb-3 uppercase tracking-tight">Actions rapides</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {actions.map(([type, o]) => {
                const Icon = ICONS[o.icon] || Truck;
                return (
                  <button
                    key={type}
                    onClick={() => openOp(type)}
                    data-testid={`quick-action-${type}`}
                    className={`tactile rounded-xl border-2 p-3 sm:p-4 text-left flex flex-col gap-2 min-h-[92px] ${ACCENT_BTN[o.accent]}`}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2.2} />
                    <span className="font-display font-bold leading-tight text-sm sm:text-base">{o.short}</span>
                  </button>
                );
              })}
              {canDeclare && (
                <button
                  onClick={() => setShowDecl(true)}
                  data-testid="quick-action-STOCK_DECLARATION"
                  className="tactile rounded-xl border-2 border-blue-300 bg-blue-50 text-blue-900 hover:bg-blue-100 p-3 sm:p-4 text-left flex flex-col gap-1.5 min-h-[92px] relative"
                >
                  <Package className="h-6 w-6" strokeWidth={2.2} />
                  <span className="font-display font-bold leading-tight text-sm sm:text-base">Déclaration de stock</span>
                  {myFerme && (
                    <>
                      <span
                        className={`absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${myFerme.declared ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}
                        data-testid="declaration-status-pill"
                      >
                        {myFerme.declared ? "🟢 DÉCLARÉ" : myFerme.late ? "🔴 EN RETARD" : "🔴 À DÉCLARER"}
                      </span>
                      <span className="text-[11px] font-data text-blue-700/80" data-testid="declaration-expected-friday">
                        {myFerme.declared ? "À jour : " : "Attendue : "}
                        <span className="capitalize">{fmtFriday(myFerme.expected_friday, { weekday: "short", day: "numeric", month: "short" })}</span>
                      </span>
                    </>
                  )}
                </button>
              )}
              {canBesoin && (
                <button
                  onClick={() => setShowBesoin(true)}
                  data-testid="quick-action-BESOIN"
                  className="tactile rounded-xl border-2 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 p-3 sm:p-4 text-left flex flex-col gap-2 min-h-[92px]"
                >
                  <ClipboardList className="h-6 w-6" strokeWidth={2.2} />
                  <span className="font-display font-bold leading-tight text-sm sm:text-base">Déclaration de besoin</span>
                </button>
              )}
            </div>
          </div>
        )}

        {tabs.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
            Vous n'avez accès à aucun module. Contactez un administrateur.
          </div>
        ) : (
        <Tabs defaultValue={tabs[0]} className="w-full">
          <TabsList className="grid h-12 bg-slate-200" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
            {tabs.includes("stocks") && <TabsTrigger value="stocks" className="font-bold" data-testid="tab-stocks">Stocks</TabsTrigger>}
            {tabs.includes("history") && <TabsTrigger value="history" className="font-bold" data-testid="tab-history">Historique</TabsTrigger>}
            {tabs.includes("saisies") && <TabsTrigger value="saisies" className="font-bold" data-testid="tab-saisies">Saisies</TabsTrigger>}
          </TabsList>

          {tabs.includes("stocks") && (
          <TabsContent value="stocks" className="space-y-4 mt-4">
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="h-11 border-2 tactile" onClick={() => download("/export/stocks.csv", "stocks.csv")} data-testid="export-csv-button">
                <FileDown className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button variant="outline" className="h-11 border-2 tactile" onClick={() => download("/export/stocks.pdf", "stocks.pdf")} data-testid="export-pdf-button">
                <FileText className="h-4 w-4 mr-2" /> PDF
              </Button>
            </div>
            <MatrixStockTable
              matrix={matrix.filter((m) => gestionCodes.has(m.site))}
              title="Gestion des emballages sales / propres"
              testid="stock-matrix-gestion"
              showSeuil={false}
              loading={stocksLoading}
            />
            <MatrixStockTable
              matrix={matrix.filter((m) => !gestionCodes.has(m.site))}
              title="Distribution d'emballages"
              testid="stock-matrix-distribution"
              loading={stocksLoading}
            />
          </TabsContent>
          )}

          {tabs.includes("history") && (
          <TabsContent value="history" className="space-y-4 mt-4">
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="h-11 border-2 tactile" onClick={() => download("/export/movements.csv", "mouvements.csv")} data-testid="export-mov-csv-button">
                <FileDown className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button variant="outline" className="h-11 border-2 tactile" onClick={() => download("/export/movements.pdf", "mouvements.pdf")} data-testid="export-mov-pdf-button">
                <FileText className="h-4 w-4 mr-2" /> PDF
              </Button>
            </div>
            <HistoryCharts sites={sites} types={types} />
          </TabsContent>
          )}

          {tabs.includes("saisies") && (
          <TabsContent value="saisies" className="mt-4">
            <MovementsList onEdit={openEdit} refreshKey={refreshKey} onChanged={bump}
              canEditFn={(m) => can(OP_MODULE[m.type], "edit")}
              canDeleteFn={(m) => can(OP_MODULE[m.type], "delete")} />
          </TabsContent>
          )}
        </Tabs>
        )}
      </div>

      {op && (
        <OperationDialog
          type={op}
          open={!!op}
          onOpenChange={(o) => { if (!o) { setOp(null); setEditMovement(null); } }}
          onDone={() => { bump(); refetch(); }}
          matrix={matrix}
          types={types}
          sites={sites}
          user={user}
          editMovement={editMovement}
        />
      )}

      <StockDeclarationDialog
        open={showDecl}
        onOpenChange={setShowDecl}
        fermeName={myFerme?.site_name}
        onDone={() => { refetchDecl(); bump(); refetch(); }}
      />

      <Dialog open={!!showReminderPopup} onOpenChange={(o) => { if (!o) dismissReminder(); }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-md" data-testid="friday-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <BellRing className="h-6 w-6 text-blue-700" /> Rappel du vendredi
            </DialogTitle>
            <DialogDescription>
              Nous sommes vendredi : pensez à déclarer le stock d'emballages de <strong>{myFerme?.site_name}</strong> (palettes, intercalaires, alvéoles) en piles.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-12" onClick={dismissReminder} data-testid="friday-reminder-later">Plus tard</Button>
            <Button className="h-12 bg-blue-800 hover:bg-blue-900 text-white font-bold" data-testid="friday-reminder-declare"
              onClick={() => { dismissReminder(); setShowDecl(true); }}>
              Déclarer maintenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BesoinDialog
        open={showBesoin}
        onOpenChange={setShowBesoin}
        fermeName={myFerme?.site_name}
      />
    </AppShell>
  );
}
